if ( typeof SearchForm !== 'function' ) {
	class SearchForm extends HTMLElement {

		constructor() {

			super();	
			this.cachedResults = {};
			this.input = this.querySelector('[data-js-search-input]');
			this.predictiveSearchResults = this.querySelector('[data-js-search-results]');
			this.results = [];
			this.preloader = false;

			// Scriptet indlaeses nu paa alle sider (theme.liquid, defer), saa et
			// <search-form> uden felt maa ikke kunne vaelte konstruktoeren.
			if ( this.input && KROWN.settings.predictive_search_enabled != "false" ) {

				let inputValue = this.input.value;

				// 'input' frem for 'keyup': keyup misser indsat tekst (hoejreklik →
				// Indsaet), mobil-IME/autocorrect, browserens autofyld og x-knappen
				// i <input type="search">. Alt det gav et felt med tekst og en tom
				// dropdown. Piletaster/Enter haandteres fortsat paa keyup nedenfor.
				const onInput = debounce(e=>{
					const value = this.input.value;
					if ( value.trim() == '' ) {
						inputValue = value;
						this.clearSearchResults();
					} else if ( value != inputValue ) {
						inputValue = value;
						this.getSearchResults(value.trim());
					}
				},150);
				this.input.addEventListener('input', onInput);

				// Upgrade-kapload: scriptet kan naa at koere EFTER kunden er
				// begyndt at skrive (defer + evt. lazy-injection fra
				// section-header.js). De tegn gav aldrig et 'input'-event vi saa,
				// saa uden dette stod dropdown'en tom indtil naeste tastetryk —
				// det var fejlen "soegningen virker ikke lige efter page load".
				// Kun naar feltet rent faktisk har fokus: ellers ville en
				// browser-genskabt vaerdi (tilbage-navigation/bfcache) aabne
				// dropdown'en af sig selv paa en side kunden ikke soegte paa.
				if ( this.input.value.trim() != '' && document.activeElement === this.input ) {
					this.getSearchResults(this.input.value.trim());
				}

				this.input.addEventListener('keydown', e=>{
					if ( this.a11yIndex != -1 ) {
						if (
							e.keyCode === window.KEYCODES.UP ||
							e.keyCode === window.KEYCODES.DOWN ||
							e.keyCode === window.KEYCODES.RETURN
						) {
							e.preventDefault();
						}
					}
				});

				this.input.addEventListener('keyup', e=>{
					if (e.keyCode === window.KEYCODES.UP) {
						this.navigateThrough('UP');
						return true;
					}

					if (e.keyCode === window.KEYCODES.DOWN) {
						this.navigateThrough('DOWN');
						return true;
					}

					if (e.keyCode === window.KEYCODES.RETURN) {
						if ( this.a11yIndex != -1 ) {
							document.location.href = this.results[this.a11yIndex].tagName == "A" ? this.results[this.a11yIndex].href : this.results[this.a11yIndex].querySelector('a').href;
						}
					}
				})

			}

		}

		async getSearchResults(query="") {

			this.preloadSearchResults();

			const queryKey = query.replace(" ", "-").toLowerCase();
			if (this.cachedResults[queryKey]) {
				this.renderSearchResults(this.cachedResults[queryKey]);
				return;
			}

			this.a11yIndex = -1;

			// Brand-aliasser foerst (fx "k#rwa" -> "kurwa", se snippets/search-aliases.liquid).
			// Skal ligge FOER alt andet: Shopify tokeniserer # vaek, saa den raa
			// query giver 0 hits og trunkerings-fallbacken nedenfor ville lede
			// efter "k#r" i stedet for at finde brandet.
			const aliased = window.KROWN_SEARCH_ALIASES ? window.KROWN_SEARCH_ALIASES.rewrite(query) : query;

			try {
				// Try original query first
				let { html, hasResults } = await this._fetchSearchResults(aliased);

				if (!hasResults) {
					// Try with normalized characters (æ→a, ø→o, å→a, etc.)
					const normalized = this._normalizeQuery(aliased);
					if (normalized !== aliased) {
						({ html, hasResults } = await this._fetchSearchResults(normalized));
					}
				}

				if (!hasResults && aliased.length > 2) {
					// Fallback: trim up to 2 characters from normalized query
					const normalized = this._normalizeQuery(aliased);
					for (let i = 1; i <= 2 && !hasResults && normalized.length - i >= 2; i++) {
						({ html, hasResults } = await this._fetchSearchResults(normalized.slice(0, -i)));
					}
				}

				this.cachedResults[queryKey] = html;
				this.renderSearchResults(html);
			} catch(e) {
				this.clearSearchResults();
				throw e;
			}

		}

		async _fetchSearchResults(query) {
			// limit_scope=each: Shopifys standard er 10 resultater I ALT delt mellem
			// brands/forslag/produkter, saa produkter fik kun 3-4 pladser naar mange
			// forslag matchede. Nu faar hver type 10; visningen cappes i Liquid.
			const response = await fetch(`${KROWN.settings.routes.predictive_search_url}?q=${encodeURIComponent(query)}&resources[type]=product,collection,page,article,query&resources[limit]=10&resources[limit_scope]=each&section_id=helper-predictive-search`);
			if (!response.ok) throw new Error(response.status);
			const text = await response.text();
			const doc = new DOMParser().parseFromString(text, 'text/html');
			const section = doc.querySelector('#shopify-section-helper-predictive-search');
			return {
				html: section.innerHTML,
				hasResults: section.querySelectorAll('.search-results [data-js-search-item]').length > 0
			};
		}

		_normalizeQuery(query) {
			const charMap = {
				'æ': 'a', 'ø': 'o', 'å': 'a',
				'Æ': 'A', 'Ø': 'O', 'Å': 'A',
				'é': 'e', 'è': 'e', 'ê': 'e', 'ë': 'e',
				'ü': 'u', 'ö': 'o', 'ä': 'a'
			};
			return query.replace(/[æøåÆØÅéèêëüöä]/g, c => charMap[c] || c);
		}

		clearSearchResults(){
			this.predictiveSearchResults.innerHTML = '';
			this.preloader = false;
			this.results = [];
		}

		preloadSearchResults(){
			if ( ! this.preloader ) {
				this.preloader = true;
				this.predictiveSearchResults.innerHTML = KROWN.settings.predictive_search_placeholder;
			}
		}

		renderSearchResults(results){
			this.predictiveSearchResults.innerHTML = results;
			this.preloader = false;
			this.results = this.predictiveSearchResults.querySelectorAll('[data-js-search-item]');
		}

		navigateThrough(direction){

			if ( this.a11yIndex == -1 ) {
				this.a11yIndex = 0;
			} else {
				this.results[this.a11yIndex].classList.remove('active');
				if ( direction == 'UP' ) {
					if ( this.a11yIndex - 1 >= -1 ) {
						this.a11yIndex--;
					} 
				} else if ( direction == 'DOWN' ) {
					if ( this.a11yIndex + 1 < this.results.length ) {
						this.a11yIndex++;
					} 
				}
			}

			if ( this.a11yIndex >= 0 ) {
				this.results[this.a11yIndex].classList.add('active');
				document.querySelector('[data-js-search-results-holder]').scrollTop = this.results[this.a11yIndex].offsetTop - 200;
			} else {
				document.querySelector('[data-js-search-results-holder]').scrollTop = 0;
			}

		}

	}

  if ( typeof customElements.get('search-form') == 'undefined' ) {
		customElements.define('search-form', SearchForm);
	}

}